/**
 * Inventory change log — who changed a product or a unit, when, and what moved.
 *
 * The server appends an entry on every inventory write (see inventory_audit_log);
 * this is the read side, used by the Log tab on the inventory page. Paging is
 * keyset-based: pass the `nextCursor` from the previous page to continue.
 */

import { API_BASE } from '../config/apiBase';

export type InventoryAuditEntityType = 'product' | 'unit';
export type InventoryAuditAction = 'created' | 'updated' | 'deleted';

export interface InventoryAuditChange {
  field: string;
  label: string;
  /** Previous value; null when the field was not set (or the row is new). */
  from: string | null;
  /** New value; null when the field was cleared (or the row was deleted). */
  to: string | null;
}

export interface InventoryAuditEntry {
  id: number;
  occurredAt: string;
  userId: number | null;
  /** Null when the change could not be attributed to a signed-in user. */
  userName: string | null;
  entityType: InventoryAuditEntityType;
  entityId: string;
  /** Product name, or a unit's serial number. */
  entityLabel: string | null;
  productId: string | null;
  productName: string | null;
  action: InventoryAuditAction;
  changes: InventoryAuditChange[];
  /** Which flow wrote the change: inventory, shipment, installation. */
  source: string | null;
}

export interface InventoryAuditPage {
  entries: InventoryAuditEntry[];
  nextCursor: number | null;
}

export interface InventoryAuditFilters {
  limit?: number;
  cursor?: number | null;
  entityType?: InventoryAuditEntityType | '';
  action?: InventoryAuditAction | '';
  productId?: string;
  userId?: number | null;
  search?: string;
}

async function readError(res: Response, fallback: string): Promise<Error> {
  let msg = fallback;
  try {
    const data = await res.json();
    if (data?.error) msg = data.error;
  } catch {
    /* ignore non-JSON */
  }
  return new Error(msg);
}

export async function fetchInventoryAuditLog(filters: InventoryAuditFilters = {}): Promise<InventoryAuditPage> {
  const params = new URLSearchParams();
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.cursor) params.set('cursor', String(filters.cursor));
  if (filters.entityType) params.set('entityType', filters.entityType);
  if (filters.action) params.set('action', filters.action);
  if (filters.productId) params.set('productId', filters.productId);
  if (filters.userId) params.set('userId', String(filters.userId));
  if (filters.search?.trim()) params.set('search', filters.search.trim());

  const res = await fetch(`${API_BASE}/api/inventory/audit-log?${params.toString()}`);
  if (!res.ok) throw await readError(res, 'Failed to load the inventory log');
  const data = await res.json();
  return {
    entries: Array.isArray(data.entries) ? data.entries : [],
    nextCursor: typeof data.nextCursor === 'number' ? data.nextCursor : null,
  };
}

/** Everyone who appears in the log, for the "changed by" filter. */
export async function fetchInventoryAuditUsers(): Promise<Array<{ id: number; name: string }>> {
  const res = await fetch(`${API_BASE}/api/inventory/audit-log/users`);
  if (!res.ok) throw await readError(res, 'Failed to load log users');
  const data = await res.json();
  return Array.isArray(data.users) ? data.users : [];
}
